/**
 * Core authentication functions for the orchestrator
 * Handles contact submission, OTP verification, password management
 */

import type { AuthPhase } from '@/types/profileTypes';
import type {
  AuthSignUpMutation,
  AuthChangePasswordMutation,
  AuthAddPasswordToAccountMutation,
  SignUpInput,
  ChangePasswordInput,
  AddPasswordToAccountInput,
  AuthUserOutput
} from '@/generated/nexus-graphql';
import { setRefreshToken } from '@/services/secureStorage';
import { startTokenRefreshTimer } from '@/utils/tokenRefreshTimer';
import type { AuthOrchestratorState } from './useAuthState';
import type { NavigationService } from './useAuthNavigationService';
import { ERROR_MESSAGES, SUCCESS_MESSAGES } from '../constants';
import { createDebugLogger } from '../utils/debug';
import { validateContact, validateOTP, validatePassword } from '../utils/validation';

const logger = createDebugLogger('AuthCore');

interface AuthFlowState {
  phase: string;
  message?: string;
  newUser?: boolean;
}

export function useAuthCore(
  state: AuthOrchestratorState,
  deps: {
    authFlow: {
      startAuthFlow: (contact: string, isEmail: boolean) => Promise<void>;
      verify: (code: string) => Promise<void>;
      loginWithPassword: (password: string) => Promise<void>;
      forgotPassword: () => Promise<boolean>;
      resend: () => Promise<void>;
      authFlowState: { value: AuthFlowState };
    };
    signUpMutate: (params: { input: SignUpInput }) => Promise<{
      data?: AuthSignUpMutation;
      errors?: Array<{ message: string }>;
    }>;
    changePasswordMutate: (params: { input: ChangePasswordInput }) => Promise<{
      data?: AuthChangePasswordMutation;
      errors?: Array<{ message: string }>;
    }>;
    addPasswordMutate: (params: { input: AddPasswordToAccountInput }) => Promise<{
      data?: AuthAddPasswordToAccountMutation;
      errors?: Array<{ message: string }>;
    }>;
    onNexusLogin: (token: string) => Promise<void>;
    loginUser: (user: AuthUserOutput) => Promise<void>;
    handleAuthComplete: () => void;
    navigationService?: NavigationService;
    // Express enroll: on new-user OTP verify, save the stashed name + 'None' role
    // and complete — skipping setPassword / alternateContact / role profiles.
    onExpressComplete?: () => Promise<void>;
  }
) {
  /**
   * Submit contact (email or phone) to start auth flow
   */
  const submitContact = async (
    contact: string,
    isEmail: boolean,
    firstName?: string,
    lastName?: string
  ) => {
    logger.log('Submitting contact, isEmail:', isEmail);

    // Validate contact before submission
    const validation = validateContact(contact);
    if (!validation.isValid) {
      state.error = validation.errors.join('. ');
      logger.error('Contact validation failed:', validation.errors);
      return;
    }

    // Express enroll: stash the name captured on panel 1 so the post-OTP
    // express completion can persist it via submitGeneralProfile.
    if (state.authMode === 'express' && (firstName || lastName)) {
      state.profileData.general = {
        ...(state.profileData.general || {}),
        firstName: (firstName || '').trim(),
        lastName: (lastName || '').trim(),
        userTypes: ['None']
      } as any;
    }

    logger.log('Current auth flow phase before start:', deps.authFlow.authFlowState.value.phase);
    state.contact = contact;
    state.isEmail = isEmail;
    state.loading = true;
    state.error = null;

    try {
      await deps.authFlow.startAuthFlow(contact, isEmail);
      logger.log('Auth flow started, new phase:', deps.authFlow.authFlowState.value.phase);

      // Manually check and update phase since watcher might not fire in Vue 2
      const newPhase = deps.authFlow.authFlowState.value.phase;
      if (newPhase === 'otp' || newPhase === 'signupOtp') {
        logger.log('Manually setting orchestrator phase to otp');
        if (deps.navigationService) {
          await deps.navigationService.navigate('otp');
        } else {
          state.phase = 'otp'; // Fallback
        }
        state.isNewUser = newPhase === 'signupOtp';
      } else if (newPhase === 'error') {
        // The flow records a failed code request as { phase: 'error', message } without
        // throwing (e.g. the server's "Please wait N seconds before requesting another
        // code."). Surface it under the field; MEASURED 2026-09-14 on prod: a second
        // submit of the same email left the sheet on the contact step with no message.
        state.error = deps.authFlow.authFlowState.value.message || ERROR_MESSAGES.GENERIC;
        logger.warn('Contact submission refused:', state.error);
      }

      state.loading = false;
    } catch (error) {
      logger.error('Contact submission failed:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC;
      state.loading = false;
    }
  };

  /**
   * Submit OTP code for verification
   */
  const submitOtp = async (code: string) => {
    logger.log('Submitting OTP');

    // Validate OTP format
    const validation = validateOTP(code);
    if (!validation.isValid) {
      state.error = validation.errors.join('. ');
      logger.error('OTP validation failed:', validation.errors);
      return;
    }

    state.loading = true;
    state.error = null;

    try {
      await deps.authFlow.verify(code);
      logger.log('OTP verified, auth flow phase:', deps.authFlow.authFlowState.value.phase);

      // Manually check and update phase since watcher might not fire in Vue 2
      const newPhase = deps.authFlow.authFlowState.value.phase;
      if (newPhase === 'error') {
        state.error = deps.authFlow.authFlowState.value.message || ERROR_MESSAGES.INVALID_OTP;
      } else if (newPhase === 'complete') {
        logger.log('Auth complete, transitioning to profile phase');

        // Use the auth flow's newUser flag instead of state.isNewUser
        const isNewUser = deps.authFlow.authFlowState.value.newUser;
        logger.log('Is new user from auth flow:', isNewUser);

        // Express enroll: new user is now authenticated (token set). Skip
        // setPassword / alternateContact / role profiles — persist the stashed
        // name + 'None' role and complete straight through.
        if (isNewUser && state.authMode === 'express' && deps.onExpressComplete) {
          logger.log('Express mode — completing without password/role phases');
          await deps.onExpressComplete();
          return;
        }

        if (isNewUser) {
          if (deps.navigationService) {
            await deps.navigationService.navigate('setPassword');
          } else {
            state.phase = 'setPassword'; // Fallback
          }
        } else {
          logger.log('Existing user - calling handleAuthComplete');
          deps.handleAuthComplete();
        }
      }
    } catch (error) {
      logger.error('OTP verification failed:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.INVALID_OTP;
    } finally {
      // Always reset loading state
      state.loading = false;
    }
  };

  /**
   * Submit password for login
   */
  const submitPassword = async (password: string) => {
    logger.log('Submitting password');
    state.loading = true;
    state.error = null;

    try {
      await deps.authFlow.loginWithPassword(password);
      logger.log('Password login successful, calling handleAuthComplete');

      // On successful password login, trigger completion flow
      deps.handleAuthComplete();
      state.loading = false;
    } catch (error) {
      logger.error('Password login failed:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.INVALID_CREDENTIALS;
      state.loading = false;
    }
  };

  /**
   * Submit password for signup
   */
  const submitPasswordSignup = async (password: string) => {
    logger.log('Submitting password signup');

    // Validate password strength
    const validation = validatePassword(password);
    if (!validation.isValid) {
      state.error = validation.feedback.join('. ');
      logger.error('Password validation failed:', validation.feedback);
      return;
    }

    state.loading = true;
    state.error = null;

    try {
      // Sign up with password
      const { data } = await deps.signUpMutate({
        input: {
          method: state.isEmail ? 'EMAIL' : 'PHONE',
          email: state.isEmail ? state.contact : undefined,
          phoneNumber: state.isEmail ? undefined : state.contact,
          password
        }
      });

      if (!data?.authSignUp?.success) {
        throw new Error(data?.authSignUp?.message || 'Signup failed');
      }

      // Login the user after successful signup
      await deps.onNexusLogin(data.authSignUp.accessToken);
      if (data.authSignUp.refreshToken) setRefreshToken(data.authSignUp.refreshToken);
      startTokenRefreshTimer();
      await deps.loginUser(data.authSignUp.user);

      // After password signup, continue to alternate contact phase for new users
      if (deps.navigationService) {
        await deps.navigationService.navigate('alternateContact');
      } else {
        state.phase = 'alternateContact'; // Fallback
      }
      state.loading = false;
    } catch (error) {
      logger.error('Password signup failed:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC;
      state.loading = false;
    }
  };

  /**
   * Set password for new user (add password to OTP account)
   */
  const setPassword = async (pwd: string) => {
    // Validate password strength
    const validation = validatePassword(pwd);
    if (!validation.isValid) {
      state.error = validation.feedback.join('. ');
      logger.error('Password validation failed:', validation.feedback);
      return;
    }

    state.loading = true;
    state.error = null;
    try {
      // For OTP users, we need to add password authentication to their account
      // This requires the email address along with the password
      const result = await deps.addPasswordMutate({
        input: {
          email: state.contact, // User's email address from the contact field
          password: pwd
        }
      });

      // Check for GraphQL errors or unsuccessful response
      if (result?.errors || !result?.data?.authAddPasswordToAccount?.success) {
        const errorMessage =
          result?.errors?.[0]?.message ||
          result?.data?.authAddPasswordToAccount?.message ||
          ERROR_MESSAGES.GENERIC;
        logger.error('Failed to add password authentication:', errorMessage);
        throw new Error(errorMessage);
      }

      logger.log('Password authentication added successfully');
      // After adding password, go to alternate contact for new users
      if (deps.navigationService) {
        await deps.navigationService.navigate('alternateContact');
      } else {
        state.phase = 'alternateContact'; // Fallback
      }
    } catch (error) {
      logger.error('Failed to add password authentication:', error);
      state.error = error instanceof Error ? error.message : ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Skip setting password
   */
  const skipSetPassword = async () => {
    // After skipping password, go to alternate contact for new users
    if (deps.navigationService) {
      await deps.navigationService.navigate('alternateContact');
    } else {
      state.phase = 'alternateContact'; // Fallback
    }
  };

  /**
   * Request password reset
   */
  const requestPasswordReset = async () => {
    logger.log('Requesting password reset');
    state.loading = true;
    state.error = null;
    try {
      const ok = await deps.authFlow.forgotPassword();
      if (ok) {
        // Re-use error banner UI to surface the info message
        state.error = SUCCESS_MESSAGES.PASSWORD_RESET_SENT;
      } else {
        state.error = ERROR_MESSAGES.GENERIC;
      }
    } catch (error) {
      logger.error('Password reset request failed:', error);
      state.error = ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Resend OTP code
   */
  const resendOtp = async () => {
    logger.log('Resending OTP');
    state.loading = true;
    state.error = null;
    try {
      await deps.authFlow.resend();
      // Show success message (using error field for simplicity)
      state.error = SUCCESS_MESSAGES.OTP_RESENT;
    } catch (error) {
      logger.error('Failed to resend OTP:', error);
      state.error = ERROR_MESSAGES.GENERIC;
    } finally {
      state.loading = false;
    }
  };

  /**
   * Alias for forgotPassword (for backward compatibility)
   */
  const { forgotPassword } = deps.authFlow;

  return {
    submitContact,
    submitOtp,
    submitPassword,
    submitPasswordSignup,
    setPassword,
    skipSetPassword,
    requestPasswordReset,
    resendOtp,
    forgotPassword
  };
}
